
import { useState, useEffect, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  getCountrySuggestions,
  getCountryCodeByName,
} from "@/utils/countryData";
import { X } from "lucide-react";
import Hey from "@/lib/hey";
import debounce from "lodash/debounce";
import getCode from 'country-flag-icons/unicode'
import { useTranslation } from "react-i18next";


interface LocationFieldsProps {
  city: string;
  country: string;
  onCityChange: (value: string) => void;
  onCountryChange: (value: string) => void;
}

interface CountrySuggestion {
  name: string;
  code: string;
}

const LocationFields = ({
  city,
  country,
  onCityChange,
  onCountryChange,
}: LocationFieldsProps) => {
  const { t } = useTranslation();
  const [countryInput, setCountryInput] = useState(country || "");
  const [cityInput, setCityInput] = useState(city || "");
  const [citysuggestions, setCitySuggestions] = useState<string[]>([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [countrysuggestions, setCountrySuggestions] = useState<CountrySuggestion[]>([]);
  const [showCountrySuggestions, setShowCountrySuggestions] = useState(false);

  const debouncedFetchCities = useCallback(
    debounce(async (keyword: string, countryName: string) => {
      if (!countryName) return;

      try {
        const countryCode = getCountryCodeByName(countryName);
        if (!countryCode) {
          console.error("Invalid country name:", countryName);
          return;
        }

        const heyInstance = Hey.HeyUtils.getInstance();
        const cities = await heyInstance.getCitiesByCountry(
          keyword,
          countryCode
        );
        console.debug("Received cities:", cities);

        setCitySuggestions(cities);
        setShowCitySuggestions(cities.length > 0);
      } catch (error) {
        console.error("Failed to fetch cities:", error);
        setCitySuggestions([]);
        setShowCitySuggestions(false);
      }
    }, 300),
    []
  );

  const handleCityInputChange = (value: string) => {
    console.debug("City input changed:", value);
    setCityInput(value);
    console.debug("country value:", country );
    console.debug("country value:", countryInput );
    
    if (countryInput && value.length >= 2) {
      console.debug(
        "Triggering city fetch for:",
        value,
        "in country:",
        countryInput
      );
      debouncedFetchCities(value, countryInput);
    } else {
      setCitySuggestions([]);
      setShowCitySuggestions(false);
    }
  };

  const clearCity = () => {
    setCityInput("");
    setCitySuggestions([]);
    setShowCitySuggestions(false);
    onCityChange("");
  };

  const handleCountryInputChange = (value: string) => {
    setCountryInput(value);
    // Clear city when country input changes
    clearCity();
    
    // Don't update parent state here, only update suggestions
    if (value.length >= 2) {
      const suggestions = getCountrySuggestions(value).map(countryName => ({
        name: countryName,
        code: getCountryCodeByName(countryName)
      }));
      setCountrySuggestions(suggestions);
      setShowCountrySuggestions(true);
    } else {
      setCountrySuggestions([]);
      setShowCountrySuggestions(false);
    }
  };

  const handleCountrySelect = (selectedCountry: string) => {
    console.debug("🌍 DEBUG: Selecting country:", selectedCountry);
    setCountryInput(selectedCountry);
    onCountryChange(selectedCountry);
    setShowCountrySuggestions(false);
    setCountrySuggestions([]);
    // Clear city when country is selected
    clearCity();
    console.debug("🌍 DEBUG: Updated country input:", selectedCountry);
  };

  const clearCountry = () => {
    setCountryInput("");
    setCountrySuggestions([]);
    setShowCountrySuggestions(false);
    clearCity(); // Also clear city when country is cleared
    onCountryChange("");
  };

  useEffect(() => {
    setCountryInput(country || "");
  }, [country]);

  useEffect(() => {
    setCityInput(city || "");
  }, [city]);

  return (
    <>
      <div className="space-y-2 relative">
        <Label htmlFor="country">{t("settings.profile.country")}</Label>
        <div className="relative">
          {countryInput && getCountryCodeByName(countryInput) && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg">
              {getCode(getCountryCodeByName(countryInput))}
            </span>
          )}
          <Input
            id="country"
            value={countryInput}
            onChange={(e) => handleCountryInputChange(e.target.value)}
            placeholder={t("settings.profile.selectCountry")}
            autoComplete="off"
            className={`pr-10 ${countryInput && getCountryCodeByName(countryInput) ? 'pl-10' : ''}`}
          />
          {countryInput && (
            <button
              type="button"
              onClick={clearCountry}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {showCountrySuggestions && countrysuggestions.length > 0 && (
          <div className="absolute z-10 w-full bg-background border rounded-md shadow-lg mt-1 max-h-[200px] overflow-y-auto">
          {countrysuggestions.map((suggestion) => (
            <div
              key={suggestion.name}
              className="px-4 py-2 hover:bg-accent cursor-pointer"
              onClick={() => {
                console.debug("Selected Country:", suggestion.name);
                setCountryInput(suggestion.name);
                onCountryChange(suggestion.name);
                setCountrySuggestions([]);
                setShowCountrySuggestions(false);
              }}
            >
              <span className="text-lg">
                  {suggestion.code ? getCode(suggestion.code) : ''}
                </span>
              <span>{suggestion.name}</span>
            </div>
          ))}
        </div>
        )}
      </div>
      <div className="space-y-2 relative">
        <Label htmlFor="city">{t("settings.profile.city")}</Label>
        <div className="relative">
          <Input
            id="city"
            value={cityInput}
            onChange={(e) => handleCityInputChange(e.target.value)}
            placeholder={t("settings.profile.enterCity")}
            autoComplete="off"
            disabled={!countryInput}
            className="pr-10"
          />
          {cityInput && (
            <button
              type="button"
              onClick={clearCity}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {showCitySuggestions && citysuggestions.length > 0 && (
          <div className="absolute z-10 w-full bg-background border rounded-md shadow-lg mt-1 max-h-[200px] overflow-y-auto">
            {citysuggestions.map((suggestion, index) => (
              <div
                key={index}
                className="px-4 py-2 hover:bg-accent cursor-pointer"
                onClick={() => {
                  console.debug("Selected city:", suggestion);
                  setCityInput(suggestion);
                  onCityChange(suggestion);
                  setCitySuggestions([]);
                  setShowCitySuggestions(false);
                }}
              >
                {suggestion}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default LocationFields;
